
import React, {
    forwardRef,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Box, HStack, Text, VStack, Input } from "@chakra-ui/react";
import {
    LuChevronDown,
    LuChevronUp,
    LuCheck,
    LuSearch,
} from "react-icons/lu";

const CustomDropdown = forwardRef(({
    label,
    options = [],
    value,
    onChange,
    placeholder = "Select",
    width = "130px",
    required = false,
    open,
    setOpen,
    height,
    background,
    fontSize,
    top,
}, ref) => {
    const dropdownRef = useRef(null);
    const optionRefs = useRef([]);
    const triggerRef = useRef(null);
    const searchInputRef = useRef(null);
    const searchTimeout = useRef(null);

    const [activeIndex, setActiveIndex] = useState(-1);
    const [search, setSearch] = useState("");
    const [searchKey, setSearchKey] = useState("");

    // Filter options using the search input.
    const filteredOptions = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (!query) return options;

        return options.filter((item) =>
            String(item).toLowerCase().includes(query)
        );
    }, [options, search]);

    // Focus the search input when the dropdown opens.
    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => {
                searchInputRef.current?.focus();
            });
        } else {
            setSearch("");
            setSearchKey("");
            setActiveIndex(-1);
        }
    }, [open]);

    // Close dropdown when clicking outside.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setOpen(false);
                triggerRef.current?.blur();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
        };
    }, [open, setOpen]);

    // Clear timers when the component unmounts.
    useEffect(() => {
        return () => clearTimeout(searchTimeout.current);
    }, []);

    // Scroll the active option into view.
    useEffect(() => {
        if (activeIndex >= 0) {
            optionRefs.current[activeIndex]?.scrollIntoView({
                block: "nearest",
            });
        }
    }, [activeIndex]);

    const handleSearchChange = (e) => {
        const query = e.target.value;

        setSearch(query);

        const matchingOptions = options.filter((item) =>
            String(item)
                .toLowerCase()
                .includes(query.trim().toLowerCase())
        );

        setActiveIndex(matchingOptions.length > 0 ? 0 : -1);
    };

    const handleKeyDown = (e) => {
        // Close dropdown.
        if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
            triggerRef.current?.focus();
            return;
        }

        // Navigate through filtered options.
        if (e.key === "ArrowDown") {
            e.preventDefault();

            if (!open) {
                setOpen(true);
                return;
            }

            setActiveIndex((prev) =>
                filteredOptions.length === 0
                    ? -1
                    : prev < filteredOptions.length - 1
                        ? prev + 1
                        : 0
            );
            return;
        }

        if (e.key === "ArrowUp") {
            e.preventDefault();

            if (!open) {
                setOpen(true);
                return;
            }

            setActiveIndex((prev) =>
                filteredOptions.length === 0
                    ? -1
                    : prev > 0
                        ? prev - 1
                        : filteredOptions.length - 1
            );
            return;
        }

        // Select highlighted option.
        if (e.key === "Enter") {
            e.preventDefault();

            if (!open) {
                setOpen(true);
                return;
            }

            if (activeIndex >= 0 && filteredOptions[activeIndex] !== undefined) {
                selectOption(filteredOptions[activeIndex]);
            }

            return;
        }

        // Alphabet shortcuts: jump to the first matching option.
        // This works on the trigger and while the search is empty.
        if (
            /^[a-zA-Z]$/.test(e.key) &&
            !e.ctrlKey &&
            !e.altKey &&
            !e.metaKey &&
            (!open || !search)
        ) {
            const char = e.key.toLowerCase();

            clearTimeout(searchTimeout.current);

            const newKey = searchKey + char;
            setSearchKey(newKey);

            searchTimeout.current = setTimeout(() => {
                setSearchKey("");
            }, 500);

            const matchingIndex = filteredOptions.findIndex((item) =>
                String(item).toLowerCase().startsWith(newKey)
            );

            if (matchingIndex !== -1) {
                setActiveIndex(matchingIndex);

                if (!open) {
                    setOpen(true);
                }
            }
        }
    };

    const selectOption = (item) => {
        onChange(item);
        setOpen(false);
        setSearch("");
        setSearchKey("");
        setActiveIndex(-1);
        triggerRef.current?.focus();
    };

    return (
        <VStack
            align="start"
            w={width}
            minW={0}
            position="relative"
            ref={dropdownRef}
            spacing={1}
        >
            {label && (
                <Text fontSize={fontSize || 12}>
                    {label}{" "}
                    {required && (
                        <sup style={{ color: "#BC3E3C" }}>*</sup>
                    )}
                </Text>
            )}

            {/* Trigger button */}
            <Box
                as="button"
                type="button"
                ref={(el) => {
                    triggerRef.current = el;

                    if (typeof ref === "function") {
                        ref(el);
                    } else if (ref) {
                        ref.current = el;
                    }
                }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(!open);
                }}
                onKeyDown={handleKeyDown}
                role="combobox"
                aria-expanded={open}
                aria-haspopup="listbox"
                fontSize="13px"
                w="100%"
                minW={0}
                textAlign="start"
                h={height || "36px"}
                border="1px solid"
                borderColor={open ? "blue.500" : "gray.300"}
                borderRadius="4px"
                bg={background || "white"}
                px={3}
                cursor="pointer"
                _hover={{ borderColor: "gray.400" }}
                _focusVisible={{
                    outline: "2px solid",
                    outlineColor: "blue.300",
                }}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                overflow="hidden"
            >
                <Text
                    fontSize="13px"
                    color={value ? "gray.700" : "gray.500"}
                    overflow="hidden"
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                    minW={0}
                >
                    {value || placeholder}
                </Text>

                {open ? <LuChevronUp /> : <LuChevronDown />}
            </Box>

            {/* Dropdown menu */}
            {open && (
                <Box
                    position="absolute"
                    top={top || "40px"}
                    left={0}
                    w="100%"
                    minW={0}
                    maxW="100%"
                    boxSizing="border-box"
                    bg="white"
                    shadow="md"
                    borderRadius="md"
                    zIndex={9999}
                    border="1px solid #e2e8f0"
                    overflow="hidden"
                >
                    {/* Search bar */}
                    <HStack
                        px={2}
                        py={2}
                        spacing={2}
                        borderBottom="1px solid #e2e8f0"
                    >
                        <LuSearch color="#718096" size={16} />

                        <Input
                            ref={searchInputRef}
                            value={search}
                            onChange={handleSearchChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Search..."
                            size="sm"
                            variant="unstyled"
                            fontSize="13px"
                            color="black"
                            autoComplete="off"
                            aria-label="Search dropdown options"
                        />
                    </HStack>

                    {/* Options list */}
                    <Box
                        role="listbox"
                        maxH="200px"
                        overflowY="auto"
                        overflowX="hidden"
                    >
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((item, index) => (
                                <HStack
                                    key={`${item}-${index}`}
                                    ref={(el) => {
                                        optionRefs.current[index] = el;
                                    }}
                                    px={3}
                                    py={2}
                                    minW={0}
                                    cursor="pointer"
                                    onMouseEnter={() =>
                                        setActiveIndex(index)
                                    }
                                    onMouseDown={(e) =>
                                        e.preventDefault()
                                    }
                                    onClick={() => selectOption(item)}
                                    bg={
                                        activeIndex === index
                                            ? "blue.100"
                                            : value === item
                                                ? "#eff1f9"
                                                : "white"
                                    }
                                    justify="space-between"
                                    spacing={2}
                                    transition="background 0.15s"
                                >
                                    <Text
                                        fontSize="12px"
                                        color="black"
                                        flex="1"
                                        minW={0}
                                        overflow="hidden"
                                        whiteSpace="nowrap"
                                        textOverflow="ellipsis"
                                        title={String(item)}
                                    >
                                        {item}
                                    </Text>

                                    {value === item && (
                                        <LuCheck
                                            size={14}
                                            color="green"
                                        />
                                    )}
                                </HStack>
                            ))
                        ) : (
                            <Text
                                fontSize="12px"
                                color="gray.500"
                                p={3}
                            >
                                No options found
                            </Text>
                        )}
                    </Box>
                </Box>
            )}
        </VStack>
    );
});

CustomDropdown.displayName = "CustomDropdown";

export default CustomDropdown;
